import { useCallback, useContext } from 'react';
import { ProductSelectionAction } from './ProductSelectionTypes';
import { LineItemKey } from '../../store/api';
import { ProductSelectionContext } from './ProductSelectionContext';
import { TYPE_WARNING } from '../notification/actions';
import { useTranslation } from 'react-i18next';
import { hasProductSelection } from './ProductSelectionUtils';
import { showNotification } from '../notification/toastNotification';

export const MAX_SELECT = 10000;

export const useProductSelection = () => {
    const contextValue = useContext(ProductSelectionContext);
    if (!contextValue) {
        throw new Error('useProductSelection must be used within a ProductSelectionProvider');
    }

    const { t } = useTranslation();
    const [state, dispatch] = contextValue;

    const selectionModeActive = hasProductSelection(state);

    const toggleSelectionMode = useCallback(() => {
        selectionModeActive
            ? dispatch({ type: ProductSelectionAction.CLOSE })
            : dispatch({ type: ProductSelectionAction.INIT_INCLUDE_SELECTION });
    }, [dispatch, selectionModeActive]);

    const cancelSelection = useCallback(() => {
        dispatch({ type: ProductSelectionAction.CLOSE });
    }, [dispatch]);

    const selectAll = useCallback(
        (totalCount: number) => {
            if (totalCount > MAX_SELECT) {
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            } else {
                dispatch({ type: ProductSelectionAction.INIT_EXCLUDE_SELECTION, totalCount });
            }
        },
        [dispatch, t]
    );

    const onSelectButtonClick = useCallback(
        (lineItemKey: LineItemKey) => {
            dispatch({ type: ProductSelectionAction.TOGGLE_KEY, key: lineItemKey });
        },
        [dispatch]
    );

    return {
        productSelection: state,
        toggleSelectionMode,
        onSelectButtonClick,
        selectAll,
        cancelSelection
    };
};
